import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  INITIAL_STATE,
  loadPersistedStateForUser,
  onboardingReducer,
  persistState,
  type OnboardingAnswers,
  type OnboardingState,
  type StepNumber,
} from './onboardingReducer';

/**
 * React hook wrapping the pure onboarding reducer + localStorage
 * persistence. Thin by design — all logic is in onboardingReducer.ts
 * so the reducer can be tested in node without React.
 *
 * Lifecycle:
 *   - On first render (client-only, guarded), hydrate state from
 *     localStorage if a valid version-matched blob exists. SSR is a
 *     no-op since there's no localStorage.
 *   - On every state change, persist (best-effort; silent failures).
 *   - Action creators are wrapped in useCallback so step components
 *     can safely use them in effect dependency arrays.
 *
 * Actions exposed:
 *   - setStep(n)  — jump to step n (1-7)
 *   - nextStep()  — advance by one
 *   - prevStep()  — retreat by one
 *   - setAnswer(patch)  — shallow-merge into answers
 *   - reset()  — back to step 1, empty answers
 */
export function useOnboardingState(userId?: string | null) {
  // SP-3 (folded filler) — hydrate ONLY from a blob stamped with (and matching)
  // this signed-in user id. A blob owned by a DIFFERENT (e.g. deleted) account
  // in the same browser, or an unstamped legacy blob, is treated as ABSENT — so
  // a fresh signup can no longer be prefilled with a prior account's typed
  // answers (incl. body metrics). Lazy init covers the case where the id is
  // already known at mount (remount after auth); the effect below covers the
  // usual null→id settle.
  const [state, dispatch] = useReducer(
    onboardingReducer,
    INITIAL_STATE,
    (init) => loadPersistedStateForUser(userId ?? null) ?? init,
  );

  // Latest state for the hydrate effect's pristine check (avoids re-running the
  // effect on every keystroke while keeping it dependency-correct).
  const stateRef = useRef(state);
  stateRef.current = state;

  // The user id we've already hydrated for. Seeded with the mount id so a
  // lazy-init hydrate isn't repeated by the effect.
  const hydratedForRef = useRef<string | null>(userId ?? null);

  // When the id settles (null → real id after auth loads), pull THIS user's own
  // persisted wizard state once — but only while the in-memory state is still
  // pristine, so a URL-param preview override (?journey / ?step) or any typed
  // progress is never clobbered.
  useEffect(() => {
    if (!userId || hydratedForRef.current === userId) return;
    const own = loadPersistedStateForUser(userId);
    const s = stateRef.current;
    const pristine = s.step === 1 && Object.keys(s.answers).length === 0;
    if (own && pristine) dispatch({ type: 'HYDRATE', state: own });
    hydratedForRef.current = userId;
  }, [userId]);

  // Skip the very first persist call — lazy init already loaded (or
  // chose) initial state; no need to write back what we just read.
  // `userId` is stamped into the blob so the Today localStorage→server
  // migration can tell this account's wizard state apart from a prior
  // (deleted) account's leftover state in the same browser. It re-fires
  // when userId settles from null → the real id after auth loads.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    persistState(state, userId);
  }, [state, userId]);

  const setStep = useCallback((step: StepNumber) => {
    dispatch({ type: 'SET_STEP', step });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: 'NEXT_STEP' });
  }, []);

  const prevStep = useCallback(() => {
    dispatch({ type: 'PREV_STEP' });
  }, []);

  const setAnswer = useCallback((patch: Partial<OnboardingAnswers>) => {
    dispatch({ type: 'SET_ANSWER', patch });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    setStep,
    nextStep,
    prevStep,
    setAnswer,
    reset,
  } satisfies {
    state: OnboardingState;
    setStep: (step: StepNumber) => void;
    nextStep: () => void;
    prevStep: () => void;
    setAnswer: (patch: Partial<OnboardingAnswers>) => void;
    reset: () => void;
  };
}
